import { useState } from "react";
import { motion } from "framer-motion";
import { FRAMEWORK } from "../../data/framework";

const NODES = [
  { x: 140, y: 420 },
  { x: 300, y: 340 },
  { x: 460, y: 260 },
  { x: 620, y: 180 },
  { x: 780, y: 100 },
];
const SUMMIT = { x: 880, y: 60 };

const FILL =
  "M-70,500 L60,500 L100,420 L180,420 L260,340 L340,340 L420,260 L500,260 L580,180 L660,180 L740,100 L820,100 L880,60 L960,160 L960,500 Z";
const RIDGE =
  "M60,500 L100,420 L180,420 L260,340 L340,340 L420,260 L500,260 L580,180 L660,180 L740,100 L820,100 L880,60 L960,160";

export const FrameworkMountain = ({ active, onActive, showLabels = true, className = "", idPrefix = "framework-node" }) => (
  <svg viewBox="-70 0 1030 500" className={`w-full h-auto ${className}`} role="img" aria-label="Summit Transformation Framework mountain diagram">
    <defs>
      <linearGradient id="fw-gold" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#B08A66" />
        <stop offset="1" stopColor="#E3CBA8" />
      </linearGradient>
    </defs>
    <polygon points="-70,500 230,240 520,500" fill="#2C3E35" opacity="0.06" />
    <polygon points="560,500 760,260 960,420 960,500" fill="#2C3E35" opacity="0.05" />
    <motion.path
      d={FILL}
      fill="url(#fw-gold)"
      opacity="0.92"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 0.92 }}
      viewport={{ once: true }}
      transition={{ duration: 1.2 }}
    />
    <motion.path
      d={RIDGE}
      fill="none"
      stroke="#2C3E35"
      strokeWidth="2.5"
      strokeLinejoin="round"
      initial={{ pathLength: 0 }}
      whileInView={{ pathLength: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.8, ease: "easeInOut" }}
    />
    {NODES.map((p, i) => {
      const phase = FRAMEWORK.phases[i];
      const isActive = active === i;
      return (
        <g
          key={i}
          className="cursor-pointer"
          onMouseEnter={() => onActive && onActive(i)}
          onClick={() => onActive && onActive(i)}
          data-testid={`${idPrefix}-${phase.n}`}
        >
          <motion.g
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 + i * 0.25, duration: 0.5, type: "spring" }}
            style={{ transformOrigin: `${p.x}px ${p.y}px` }}
          >
            <circle cx={p.x} cy={p.y} r={isActive ? 21 : 18} fill={isActive ? "#2C3E35" : "#FDFCF8"} stroke="#2C3E35" strokeWidth="2" style={{ transition: "r .2s, fill .2s" }} />
            <text x={p.x} y={p.y + 5} textAnchor="middle" fontSize="16" fontWeight="600" fill={isActive ? "#FDFCF8" : "#2C3E35"} fontFamily="Manrope, sans-serif">
              {phase.n}
            </text>
          </motion.g>
          {showLabels && (
            <motion.g
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 + i * 0.25, duration: 0.5 }}
              className="hidden md:block"
            >
              <text x={p.x + 10} y={p.y - 42} textAnchor="end" fontSize="23" fontWeight="600" fill={isActive ? "#A9825C" : "#1C1C1C"} fontFamily="Cormorant Garamond, serif" style={{ transition: "fill .2s" }}>
                {phase.title}
              </text>
              <text x={p.x + 10} y={p.y - 22} textAnchor="end" fontSize="13.5" fill="#5A5A5A" fontFamily="Manrope, sans-serif" letterSpacing="0.5">
                {phase.subtitle}
              </text>
            </motion.g>
          )}
        </g>
      );
    })}
    <motion.g
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 1.7, duration: 0.6 }}
    >
      <line x1={SUMMIT.x} y1={SUMMIT.y} x2={SUMMIT.x} y2={SUMMIT.y - 26} stroke="#A9825C" strokeWidth="2" />
      <polygon points={`${SUMMIT.x},${SUMMIT.y - 26} ${SUMMIT.x + 18},${SUMMIT.y - 20} ${SUMMIT.x},${SUMMIT.y - 14}`} fill="#A9825C" />
      <text x={SUMMIT.x - 14} y={SUMMIT.y - 32} textAnchor="end" fontSize="13.5" fontWeight="600" fill="#1C1C1C" fontFamily="Manrope, sans-serif" letterSpacing="3" className="hidden md:block">
        {FRAMEWORK.outcome.toUpperCase()}
      </text>
    </motion.g>
  </svg>
);

export const FrameworkSection = ({ dark = false, compact = false }) => {
  const [active, setActive] = useState(0);
  const phase = FRAMEWORK.phases[active];
  const text = dark ? "text-white" : "text-summit-charcoal";
  const muted = dark ? "text-white/70" : "text-summit-text-muted";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-testid="framework-graphic">
      <div className="lg:col-span-7">
        <FrameworkMountain active={active} onActive={setActive} />
      </div>
      <div className="lg:col-span-5">
        <div className="space-y-1" role="tablist" aria-label="Framework phases">
          {FRAMEWORK.phases.map((p, i) => (
            <button
              key={p.n}
              role="tab"
              aria-selected={active === i}
              data-testid={`framework-phase-${p.n}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => setActive(i)}
              className={`w-full text-left flex items-start gap-4 px-4 py-3.5 border-l-2 transition-colors ${
                active === i
                  ? `border-summit-gold ${dark ? "bg-white/5" : "bg-summit-sand/70"}`
                  : "border-transparent hover:border-summit-gold/40"
              }`}
            >
              <span className={`font-heading text-2xl leading-none mt-0.5 ${active === i ? "text-summit-gold-dark" : muted}`}>{p.n}</span>
              <span>
                <span className={`block font-heading text-xl md:text-2xl font-medium leading-tight ${text}`}>{p.title}</span>
                <span className={`block font-body text-xs uppercase tracking-[0.18em] mt-1 ${active === i ? "text-summit-gold-dark" : muted}`}>{p.subtitle}</span>
              </span>
            </button>
          ))}
        </div>
        {!compact && (
          <motion.p
            key={active}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="framework-phase-description"
            className={`mt-6 pl-4 font-body text-base leading-relaxed ${muted} min-h-[96px]`}
          >
            {phase.description}
          </motion.p>
        )}
        <div className={`mt-6 pl-4 flex items-center gap-3 ${text}`}>
          <span className="w-2 h-2 rotate-45 bg-summit-gold" />
          <span className="font-body text-xs uppercase tracking-[0.22em]">Outcome: {FRAMEWORK.outcome}</span>
        </div>
      </div>
    </div>
  );
};
